import { useFrame, useThree } from "@react-three/fiber";
import { useMemo, useRef } from "react";
import {
  BufferAttribute,
  BufferGeometry,
  DoubleSide,
  type Group,
  Shape,
  ShapeGeometry,
} from "three";
import {
  hexCellCornerPoints,
  hexToWorld,
  roundHexCoordinate,
  worldToHex,
} from "../lib/grid-utils";

interface HexGridProps {
  cellSize: number;
  fadeDistance?: number;
}

interface HexCellHighlightProps {
  position: [number, number, number];
  cellSize: number;
  color?: string;
}

function buildHexGridGeometry(cellSize: number, fadeDistance: number) {
  const size = Math.max(cellSize, 0.0001);
  const range = Math.ceil((fadeDistance || 80) / size);
  const corners = hexCellCornerPoints(size);
  const segments: number[] = [];

  for (let q = -range; q <= range; q++) {
    for (let r = -range; r <= range; r++) {
      const center = hexToWorld({ q, r, s: -q - r }, size);
      if (Math.hypot(center.x, center.z) > (fadeDistance || 80) * 1.2) {
        continue;
      }

      for (let i = 0; i < 6; i++) {
        const [x0, z0] = corners[i];
        const [x1, z1] = corners[(i + 1) % 6];
        segments.push(
          center.x + x0,
          0.001,
          center.z + z0,
          center.x + x1,
          0.001,
          center.z + z1,
        );
      }
    }
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute(
    "position",
    new BufferAttribute(new Float32Array(segments), 3),
  );
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function buildHexCellShapeGeometry(cellSize: number) {
  const size = Math.max(cellSize, 0.0001);
  const corners = hexCellCornerPoints(size);
  const shape = new Shape();
  shape.moveTo(corners[0][0], corners[0][1]);

  for (let i = 1; i < corners.length; i++) {
    shape.lineTo(corners[i][0], corners[i][1]);
  }
  shape.closePath();

  return new ShapeGeometry(shape);
}

function buildHexCellOutlineGeometry(cellSize: number) {
  const size = Math.max(cellSize, 0.0001);
  const corners = hexCellCornerPoints(size);
  const points: number[] = [];

  for (let i = 0; i < corners.length; i++) {
    const [x0, z0] = corners[i];
    const [x1, z1] = corners[(i + 1) % corners.length];
    points.push(x0, 0, z0, x1, 0, z1);
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute(
    "position",
    new BufferAttribute(new Float32Array(points), 3),
  );
  return geometry;
}

export function HexGrid({ cellSize, fadeDistance = 80 }: HexGridProps) {
  const { camera } = useThree();
  const gridRef = useRef<Group>(null);
  const geometry = useMemo(
    () => buildHexGridGeometry(cellSize, fadeDistance),
    [cellSize, fadeDistance],
  );

  useFrame(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const nearest = roundHexCoordinate(worldToHex(camera.position, cellSize));
    const center = hexToWorld(nearest, cellSize);
    grid.position.set(center.x, 0, center.z);
  });

  return (
    <group ref={gridRef}>
      <lineSegments geometry={geometry}>
        <lineBasicMaterial color="#333333" transparent opacity={0.55} />
      </lineSegments>
    </group>
  );
}

export function HexGridCellHighlight({
  position,
  cellSize,
  color = "#22c55e",
}: HexCellHighlightProps) {
  const geometry = useMemo(
    () => buildHexCellShapeGeometry(cellSize),
    [cellSize],
  );
  const outlineGeometry = useMemo(
    () => buildHexCellOutlineGeometry(cellSize),
    [cellSize],
  );

  return (
    <group position={position} rotation={[-Math.PI / 2, 0, 0]}>
      <mesh position={[0, 0.002, 0]}>
        <primitive object={geometry} attach="geometry" />
        <meshBasicMaterial
          color={color}
          transparent
          opacity={0.18}
          side={DoubleSide}
        />
      </mesh>
      <lineSegments geometry={outlineGeometry}>
        <lineBasicMaterial color={color} transparent opacity={0.5} />
      </lineSegments>
    </group>
  );
}
